import { Controller } from "../../lib/stimulus-shim"

// Contrôleur dédié aux redirections intelligentes deep links
// Spécialement optimisé pour les profils publics
export default class extends Controller {
  static targets = ["link"]
  static values = {
    webUrl: String,
    deepUrl: String,
    intentUrl: String,
    universalUrl: String,
    platform: String,
    profileId: String
  }

  connect() {
    // Analytics : track que le contrôleur est chargé sur cette page
    this.trackPageLoad()
  }

  // Action principale : gestion intelligente des clics
  redirect(event) {
    // Détection environnement
    const userAgent = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(userAgent)
    const isBot = this.isBot(userAgent)
    const isInApp = this.isInAppBrowser(userAgent)

    const webUrl = this.webUrlValue || event.currentTarget.href

    // Track link_click for analytics funnel (unified tracking)
    this.trackLinkClick(webUrl, event.currentTarget)

    // SEO : Laisser les bots et desktop suivre le lien normal
    if (!isMobile || isBot) {
      this.trackClick('web', 'seo_or_desktop')
      return true // Comportement normal du lien
    }

    // Mobile : intercepter pour deep link
    event.preventDefault()

    const deepUrl = this.deepUrlValue || event.currentTarget.dataset.deep
    const intentUrl = this.intentUrlValue || event.currentTarget.dataset.intent

    // Stratégie de redirection mobile
    this.performSmartRedirect(webUrl, deepUrl, intentUrl, userAgent, isInApp)
  }

  // Logique de redirection optimisée
  performSmartRedirect(webUrl, deepUrl, intentUrl, userAgent, isInApp) {
    if (/Android/i.test(userAgent)) {
      this.handleAndroidRedirect(webUrl, deepUrl, intentUrl, isInApp)
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      this.handleIOSRedirect(webUrl, deepUrl, isInApp)
    } else {
      // Fallback mobile générique
      this.fallbackRedirect(webUrl)
    }
  }

  // Gestion spécifique Android
  handleAndroidRedirect(webUrl, deepUrl, intentUrl, isInApp) {
    if (isInApp && intentUrl) {
      // Navigateur in-app : utiliser Intent URL
      this.trackClick('intent', 'android_in_app')
      window.location.href = intentUrl
    } else if (deepUrl) {
      // App native Android
      this.trackClick('deep_link', 'android_native')
      this.tryDeepLinkWithFallback(deepUrl, webUrl)
    } else {
      this.fallbackRedirect(webUrl)
    }
  }

  // Gestion spécifique iOS
  handleIOSRedirect(webUrl, deepUrl, isInApp) {
    if (this.universalUrlValue) {
      // Universal Link : l'OS intercepte et ouvre l'app.
      // Si pas d'app installée : Safari ouvre l'URL normalement (site web plateforme).
      // PAS de fallback/setTimeout → évite la double redirection.
      this.trackClick('universal_link', 'ios')
      window.location.href = this.universalUrlValue
    } else if (deepUrl) {
      // Legacy pour patterns sans universal_link_template
      this.trackClick('deep_link', 'ios_legacy')
      this.tryDeepLinkWithFallback(deepUrl, webUrl)
    } else {
      this.fallbackRedirect(webUrl)
    }
  }

  // Tentative deep link avec fallback intelligent
  tryDeepLinkWithFallback(deepUrl, webUrl) {
    // Sauvegarder le focus avant redirection
    const hasFocusBeforeRedirect = document.hasFocus()
    
    // Tentative d'ouverture app native
    window.location.href = deepUrl
    
    // Fallback si l'app n'est pas installée
    setTimeout(() => {
      // Si la page a encore le focus après 1.5s, l'app ne s'est pas ouverte
      if (document.hasFocus() && hasFocusBeforeRedirect) {
        this.trackClick('fallback', 'app_not_installed')
        window.open(webUrl, '_blank')
      } else {
        this.trackClick('success', 'app_opened')
      }
    }, 1500)
  }

  // Fallback : ouverture web normale
  fallbackRedirect(webUrl) {
    this.trackClick('web', 'fallback')
    window.open(webUrl, '_blank')
  }

  // Détection de bots sophistiquée
  isBot(userAgent) {
    const botPatterns = [
      /bot/i, /crawl/i, /slurp/i, /spider/i,
      /facebookexternalhit/i, /twitterbot/i, /linkedinbot/i,
      /whatsapp/i, /telegram/i, /skype/i,
      /googlebot/i, /bingbot/i, /baiduspider/i,
      /yandexbot/i, /duckduckbot/i
    ]
    
    return botPatterns.some(pattern => pattern.test(userAgent))
  }

  // Détection navigateurs in-app
  isInAppBrowser(userAgent) {
    const inAppPatterns = [
      /FBAN/i, /FBAV/i, // Facebook
      /Instagram/i, // Instagram
      /TwitterAndroid/i, // Twitter Android
      /Line/i, // Line
      /MicroMessenger/i, // WeChat
      /QQ/i, // QQ Browser
      /GSA/i // Google App
    ]
    
    return inAppPatterns.some(pattern => pattern.test(userAgent))
  }

  // Analytics : track du chargement de page
  trackPageLoad() {
    if (typeof ahoy !== 'undefined') {
      ahoy.track('deep_link_controller_loaded', {
        category: 'profiles',
        page_location: window.location.href
      })
    }
  }

  // Analytics : track link_click unifié pour le funnel visiteurs
  trackLinkClick(url, linkElement) {
    if (typeof ahoy === 'undefined') return

    const isExternal = this.isExternalLink(url)
    const linkText = linkElement.textContent?.trim() || ''

    ahoy.track('link_click', {
      profile_id: this.profileIdValue,
      link_url: url,
      link_text: linkText,
      platform: this.platformValue,
      external: isExternal.toString(),
      referrer: window.location.href
    })
  }

  // Analytics : track des clics avec contexte (deep link specifics)
  trackClick(method, context) {
    const platform = this.platformValue || 'unknown'

    if (typeof ahoy !== 'undefined') {
      ahoy.track('deep_link_click', {
        category: 'profiles',
        platform: platform,
        method: method,
        context: context,
        page_location: window.location.href
      })
    }

    // Analytics console pour debug en développement
    if (Rails.env === 'development') {
      console.log(`🔗 Deep Link: ${method} (${context}) for ${platform}`, {
        webUrl: this.webUrlValue,
        deepUrl: this.deepUrlValue,
        intentUrl: this.intentUrlValue
      })
    }
  }

  // Helper pour déterminer si un lien est externe
  isExternalLink(url) {
    if (!url) return false

    try {
      const linkUrl = new URL(url, window.location.href)
      const currentHost = window.location.hostname

      // Compare hostnames (ignore www prefix)
      const linkHost = linkUrl.hostname.replace(/^www\./, '')
      const currentHostNormalized = currentHost.replace(/^www\./, '')

      return linkHost !== currentHostNormalized
    } catch (e) {
      // Si l'URL est invalide, considérer comme externe par sécurité
      return true
    }
  }

  // Méthode utilitaire pour debug
  debugInfo() {
    return {
      userAgent: navigator.userAgent,
      isMobile: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
      isBot: this.isBot(navigator.userAgent),
      isInApp: this.isInAppBrowser(navigator.userAgent),
      hasDeepUrl: !!this.deepUrlValue,
      hasIntentUrl: !!this.intentUrlValue,
      hasUniversalUrl: !!this.universalUrlValue,
      platform: this.platformValue
    }
  }
}